import { useTranslation } from "react-i18next"

import type {
  FairUseVoiceTokenPriceRow,
  FairUseVoiceTokenPricesIssue,
} from "./fair-use-pricing-utils"
import type { FairUseVoiceTokenKind } from "@/api/admin/fair-use"

import { FAIR_USE_VOICE_TOKEN_PRICE_FIELDS } from "@/api/admin/fair-use"
import { Plus, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { fairUseVoiceTokenCellId, fairUseVoiceTokenPriceRow } from "./fair-use-pricing-utils"

type FairUseVoiceTokenPricesProps = {
  rows: FairUseVoiceTokenPriceRow[]
  /** Mirrors `PLATFORM_ONLY_CONFIG_FIELDS`: tenants read, the platform writes. */
  canEdit: boolean
  issue: FairUseVoiceTokenPricesIssue
  onChange: (rows: FairUseVoiceTokenPriceRow[]) => void
}

/** The shared key: one table for every voice model without its own. */
const DEFAULT_KEY = "default"

/**
 * What a realtime voice TOKEN costs (#3089, spec R6).
 *
 * Voice used to be the one metered surface estimated from the wall clock. The
 * realtime API reports what it billed — audio and text tokens in and out, with
 * a cheaper rate for the audio prompt it served from its own cache — so a
 * model with a table here is billed from the provider's own counts, exactly
 * like chat. The per-minute rate above remains the FALLBACK: a model with no
 * table keeps being metered per started minute, and a model with neither
 * meters a real 0.00 SEK row and logs once.
 *
 * A blank box is a kind this model is not billed for; a model needs at least
 * one rate, because a row with none would be dropped on read and silently move
 * that model back to per-minute billing under a successful save.
 *
 * Platform-only, for the obvious reason: the rate is an input to the invoice.
 */
export function FairUseVoiceTokenPrices({
  rows,
  canEdit,
  issue,
  onChange,
}: FairUseVoiceTokenPricesProps) {
  const { t } = useTranslation()

  const modelLabel = t("adminDashboard.fairUse.settings.voiceTokens.modelLabel", "Voice model")

  const kindName = (kind: FairUseVoiceTokenKind) =>
    t(`adminDashboard.fairUse.settings.voiceTokens.kinds.${kind}`, { defaultValue: kind })

  const patchRate = (index: number, kind: FairUseVoiceTokenKind, value: string) => {
    onChange(
      rows.map((row, position) =>
        position === index ? { ...row, rates: { ...row.rates, [kind]: value } } : row
      )
    )
  }

  const header = (
    <div className="space-y-1">
      <Label className="text-sm font-semibold">
        {t("adminDashboard.fairUse.settings.voiceTokens.title", "Voice token price")}
      </Label>
      <p className="text-muted-foreground max-w-prose text-xs">
        {t(
          "adminDashboard.fairUse.settings.voiceTokens.caption",
          "SEK per 1M realtime tokens, per voice model. Use “default” for one table covering every voice model; a model without one is metered per started minute instead."
        )}
      </p>
    </div>
  )

  if (!canEdit) {
    return (
      <div className="space-y-2" data-testid="fair-use-voice-token-prices-readonly">
        {header}
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("adminDashboard.fairUse.settings.voiceTokens.none", "No voice token price is set.")}
          </p>
        ) : (
          <ul className="space-y-1 text-sm">
            {rows.map((row) => (
              <li key={row.key} className="space-y-0.5">
                <span className="font-mono text-xs">{row.id}</span>
                <ul className="text-muted-foreground space-y-0.5 pl-3 text-xs">
                  {FAIR_USE_VOICE_TOKEN_PRICE_FIELDS.filter(
                    (kind) => row.rates[kind].trim() !== ""
                  ).map((kind) => (
                    <li key={kind} className="tabular-nums">
                      {kindName(kind)}:{" "}
                      {t("adminDashboard.fairUse.settings.voiceTokens.perMillionValue", {
                        defaultValue: "{{rate}} SEK / 1M",
                        rate: row.rates[kind],
                      })}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3" data-testid="fair-use-voice-token-prices">
      {header}

      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t(
            "adminDashboard.fairUse.settings.voiceTokens.emptyHint",
            "No token price yet — voice is metered per started minute until one is added."
          )}
        </p>
      ) : null}

      {rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[46rem] border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className="text-muted-foreground border-b py-2 pr-4 text-left text-xs font-semibold tracking-wider uppercase">
                  {modelLabel}
                </th>
                {FAIR_USE_VOICE_TOKEN_PRICE_FIELDS.map((kind) => (
                  <th
                    key={kind}
                    className="text-muted-foreground border-b px-2 py-2 text-left text-xs font-semibold tracking-wider uppercase"
                  >
                    {kindName(kind)}
                  </th>
                ))}
                <th className="border-b py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.key}>
                  <td className="border-b py-2 pr-4 align-middle">
                    <Input
                      aria-label={modelLabel}
                      className="h-9 w-52 font-mono text-xs"
                      id={`fair-use-voice-token-model-${index}`}
                      placeholder={DEFAULT_KEY}
                      value={row.id}
                      onChange={(event) =>
                        onChange(
                          rows.map((candidate, position) =>
                            position === index
                              ? { ...candidate, id: event.target.value }
                              : candidate
                          )
                        )
                      }
                    />
                  </td>
                  {FAIR_USE_VOICE_TOKEN_PRICE_FIELDS.map((kind) => (
                    <td key={kind} className="border-b px-2 py-2 align-middle">
                      <Input
                        aria-label={`${kindName(kind)}, ${row.id || DEFAULT_KEY}`}
                        className="h-9 w-24 tabular-nums"
                        id={fairUseVoiceTokenCellId(index, kind)}
                        inputMode="decimal"
                        value={row.rates[kind]}
                        onChange={(event) => patchRate(index, kind, event.target.value)}
                      />
                    </td>
                  ))}
                  <td className="border-b py-2 align-middle">
                    <Button
                      className="h-9"
                      size="icon"
                      type="button"
                      variant="ghost"
                      aria-label={t("adminDashboard.fairUse.settings.voiceTokens.remove", {
                        defaultValue: "Remove the token price for {{model}}",
                        model: row.id || DEFAULT_KEY,
                      })}
                      onClick={() => onChange(rows.filter((_row, position) => position !== index))}
                    >
                      <Trash2 className="text-destructive size-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <Button
        size="sm"
        type="button"
        variant="outline"
        onClick={() => onChange([...rows, fairUseVoiceTokenPriceRow()])}
      >
        <Plus className="mr-2 size-4" />
        {t("adminDashboard.fairUse.settings.voiceTokens.add", "Add a voice model")}
      </Button>

      {issue === "id" && (
        <p className="text-destructive text-xs" data-testid="fair-use-voice-token-id-error">
          {t(
            "adminDashboard.fairUse.settings.voiceTokens.idError",
            "Every row needs a voice model id — use “default” for the shared one."
          )}
        </p>
      )}
      {issue === "duplicate" && (
        <p className="text-destructive text-xs" data-testid="fair-use-voice-token-duplicate-error">
          {t(
            "adminDashboard.fairUse.settings.voiceTokens.duplicateError",
            "Two rows name the same voice model. Ids are compared without case."
          )}
        </p>
      )}
      {issue === "empty" && (
        <p className="text-destructive text-xs" data-testid="fair-use-voice-token-empty-error">
          {t(
            "adminDashboard.fairUse.settings.voiceTokens.emptyError",
            "A voice model needs at least one rate. Remove the row to meter that model per started minute instead."
          )}
        </p>
      )}
      {issue === "range" && (
        <p className="text-destructive text-xs" data-testid="fair-use-voice-token-range-error">
          {t(
            "adminDashboard.fairUse.settings.voiceTokens.rangeError",
            "A rate is 0 SEK or more, at most 10000, written with a comma or a point (1,50) and no thousands grouping."
          )}
        </p>
      )}
    </div>
  )
}
